import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api, { vaultsAPI } from '../services/api';
import { importInChunks, fetchVaultItems, waitForThread } from '../services/bulkImport';
import useOtpEntries from '../hooks/useOtpEntries';
import { splitOtpDuplicates } from '../utils/otpParsers';
import { parseImportText, toBulkItem, buildDupIndex, markDuplicates } from '../utils/importParsers';
import { Modal, PillButton, SegmentedControl, Input, Chip } from '../ui';
import { ReactComponent as UploadIcon } from '../assets/icons/upload.svg';
import { ReactComponent as DownloadIcon } from '../assets/icons/download.svg';
import { ReactComponent as CheckIcon } from '../assets/icons/check.svg';
import { ReactComponent as AlertIcon } from '../assets/icons/alert-triangle.svg';
import { ReactComponent as FileIcon } from '../assets/icons/file-text.svg';
import './ImportExport.css';

const NEW_VAULT = '__new__';
const SOURCES = ['auto', 'chrome', 'edge', 'firefox', 'safari', 'bitwarden', '1password', 'lastpass', 'securegate'];
const INSTRUCTION_KEY = { auto: 'generic', chrome: 'chrome', edge: 'edge', firefox: 'firefox', safari: 'safari', bitwarden: 'bitwarden', '1password': '1password', lastpass: 'lastpass', securegate: 'securegate' };
const SOURCE_NAME = { chrome: 'Google Chrome / Brave', edge: 'Microsoft Edge', firefox: 'Mozilla Firefox', safari: 'Safari', bitwarden: 'Bitwarden', '1password': '1Password', lastpass: 'LastPass', securegate: 'SecureGate Card' };

// Mismo criterio que Dashboard.createVault para el id de una bóveda nueva.
const vaultIdFromName = (name) => name.trim().toLowerCase().replace(/\s+/g, '-');

/**
 * Importar / exportar. `defaultVaultId`: bóveda preseleccionada como destino
 * (al abrirlo desde una bóveda). `onImportComplete` se llama al cerrar si hubo cambios.
 */
function ImportExport({ onClose, onImportComplete, defaultVaultId = 'personal', defaultTab = 'import' }) {
  const { t } = useTranslation();
  const rfidId = localStorage.getItem('rfidId');
  const otp = useOtpEntries();

  const [tab, setTab] = useState(defaultTab);
  const [source, setSource] = useState('auto');
  const [vaults, setVaults] = useState([]);
  const [vaultChoice, setVaultChoice] = useState(defaultVaultId);
  const [newVaultName, setNewVaultName] = useState('');

  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState(null); // { entries, otpEntries, error, format }
  const [dupIndex, setDupIndex] = useState(() => new Set());
  const [dupLoading, setDupLoading] = useState(false);
  const [dupMode, setDupMode] = useState('skip');

  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [formError, setFormError] = useState('');

  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [exportPct, setExportPct] = useState(0);
  const [exportError, setExportError] = useState('');

  const fileInputRef = useRef(null);
  const cancelRef = useRef(false);
  const dupCacheRef = useRef({}); // vaultId -> Set de claves

  useEffect(() => {
    if (!rfidId) return;
    vaultsAPI.list(rfidId).then((res) => setVaults(res.data?.vaults || [])).catch(() => setVaults([]));
  }, [rfidId]);

  const vaultOptions = useMemo(() => [
    { id: 'personal', name: t('dashboard.personalVault') },
    ...vaults.filter((v) => v.vaultId !== 'personal').map((v) => ({ id: v.vaultId, name: v.name })),
  ], [vaults, t]);

  // Si la bóveda preseleccionada no existe (p. ej. 'all'), cae a Personal.
  useEffect(() => {
    if (vaultChoice !== NEW_VAULT && !vaultOptions.some((o) => o.id === vaultChoice)) {
      if (vaults.length || vaultChoice === 'all') setVaultChoice('personal');
    }
  }, [vaultOptions, vaultChoice, vaults.length]);

  // Duplicados frente a la bóveda destino (nombre + usuario + URL).
  const hasEntries = !!parsed?.entries?.length;
  useEffect(() => {
    if (!hasEntries || vaultChoice === NEW_VAULT) { setDupIndex(new Set()); return undefined; }
    if (dupCacheRef.current[vaultChoice]) { setDupIndex(dupCacheRef.current[vaultChoice]); return undefined; }
    let cancelled = false;
    setDupLoading(true);
    fetchVaultItems(rfidId, vaultChoice)
      .then((items) => {
        const idx = buildDupIndex(items);
        dupCacheRef.current[vaultChoice] = idx;
        if (!cancelled) setDupIndex(idx);
      })
      .catch(() => { if (!cancelled) setDupIndex(new Set()); })
      .finally(() => { if (!cancelled) setDupLoading(false); });
    return () => { cancelled = true; };
  }, [hasEntries, vaultChoice, rfidId]);

  const rows = useMemo(() => (parsed?.entries ? markDuplicates(parsed.entries, dupIndex) : []), [parsed, dupIndex]);
  const dupCount = rows.filter((r) => r.duplicate).length;
  const toImport = dupMode === 'skip' ? rows.filter((r) => !r.duplicate) : rows;
  const otpSplit = useMemo(() => splitOtpDuplicates(parsed?.otpEntries || [], otp.entries), [parsed, otp.entries]);
  const typeCounts = useMemo(() => rows.reduce((acc, r) => ({ ...acc, [r.entry.type]: (acc[r.entry.type] || 0) + 1 }), {}), [rows]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setFormError('');
    try {
      const lower = file.name.toLowerCase();
      let text;
      if (lower.endsWith('.1pux') || lower.endsWith('.zip')) {
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(file);
        const data = zip.file('export.data');
        if (!data) throw new Error('no export.data');
        text = await data.async('string');
      } else {
        text = await file.text();
      }
      setParsed(parseImportText(text));
    } catch (err) {
      console.error('Import parse error:', err);
      setParsed({ entries: [], otpEntries: [], error: 'unknown', format: null });
    }
  };

  const handleImport = async () => {
    setFormError('');
    let vaultId = vaultChoice;
    const newName = newVaultName.trim();
    if (vaultChoice === NEW_VAULT) {
      if (!newName) { setFormError(t('importExport.vaultNameRequired')); return; }
      vaultId = vaultIdFromName(newName);
      if (vaultId !== 'personal' && vaultOptions.some((o) => o.id === vaultId)) { setFormError(t('importExport.vaultExists')); return; }
    }

    setImporting(true);
    cancelRef.current = false;
    try {
      if (vaultChoice === NEW_VAULT && vaultId !== 'personal') {
        await vaultsAPI.create({ name: newName, vaultId });
        setVaults((prev) => [...prev, { vaultId, name: newName }]);
      }

      const items = toImport.map((r) => toBulkItem(r.entry, vaultId));
      const res = items.length
        ? await importInChunks(rfidId, items, {
          onProgress: (done, total) => setProgress({ done, total }),
          shouldCancel: () => cancelRef.current,
        })
        : { success: 0, failed: 0, total: 0, cancelled: false, error: null };
      delete dupCacheRef.current[vaultId];

      // Códigos 2FA del fichero: una sola escritura, sin duplicar por secreto.
      let otpResult = null;
      if (!res.error && !res.cancelled && parsed.otpEntries.length) {
        const add = otp.addEntries(parsed.otpEntries);
        const saved = add.blocked ? false : await otp.flush();
        otpResult = { ...add, saved };
      }
      setResult({ ...res, skippedDuplicates: dupMode === 'skip' ? dupCount : 0, otp: otpResult, vaultId });
    } catch (err) {
      setResult({ success: 0, failed: toImport.length, total: toImport.length, error: err.response?.data?.error || err.message, skippedDuplicates: 0, otp: null });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setExportPct(0);
    setExportError('');
    try {
      const { data } = await api.post('/data/bulk-export', { rfidId, format: exportFormat }, { timeout: 30000 });
      const thread = await waitForThread(data.threadId, { onProgress: (pct) => setExportPct(pct), timeoutMs: 5 * 60 * 1000 });
      if (thread.status !== 'completed' || !thread.result?.data) throw new Error(thread.error || thread.status);

      const payload = thread.result.data;
      const day = new Date().toISOString().split('T')[0];
      const isCsv = exportFormat === 'csv';
      const content = isCsv && typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);
      const filename = isCsv ? `rfid-passwords-${day}.csv` : exportFormat === 'encrypted' ? `rfid-backup-${day}.rfidvault` : `rfid-passwords-${day}.json`;
      const blob = new Blob([content], { type: isCsv ? 'text/csv' : 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setExportError(err.response?.data?.error || t('importExport.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const changed = !!result && (result.success > 0 || result.otp?.added > 0);
  const close = useCallback(() => {
    if (importing) return;
    if (changed) onImportComplete?.();
    onClose?.();
  }, [importing, changed, onImportComplete, onClose]);

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const otpBlocked = !!otp.loadError;
  const canImport = !!parsed && !parsed.error && (toImport.length > 0 || otpSplit.fresh.length > 0)
    && !importing && !otp.loading && !dupLoading && !result;

  const fileError = parsed?.error === 'encrypted' ? t('importExport.encrypted')
    : parsed && (parsed.error || (!parsed.entries.length && !parsed.otpEntries.length)) ? t('importExport.nothingFound') : '';

  const exportFormats = [
    { id: 'csv', name: 'CSV', description: t('importExport.csvDesc') },
    { id: 'json', name: 'JSON', description: t('importExport.jsonDesc') },
    { id: 'encrypted', name: t('importExport.encryptedBackup'), description: t('importExport.encryptedDesc') },
  ];

  return (
    <Modal open onClose={close} title={t('importExport.title')} className="sgc-import">
      <SegmentedControl
        value={tab}
        onChange={setTab}
        options={[
          { value: 'import', label: t('importExport.import'), icon: UploadIcon },
          { value: 'export', label: t('importExport.export'), icon: DownloadIcon },
        ]}
      />

      {tab === 'import' && (
        <div className="sgc-import__body">
          {/* 1. Destino */}
          <div className="sgc-import__field">
            <label className="sgc-import__label" htmlFor="sgc-import-vault">{t('importExport.destination')}</label>
            <select
              id="sgc-import-vault"
              className="sgc-import__select"
              value={vaultChoice}
              disabled={importing || !!result}
              onChange={(e) => setVaultChoice(e.target.value)}
            >
              {vaultOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              <option value={NEW_VAULT}>{t('importExport.newVault')}</option>
            </select>
            {vaultChoice === NEW_VAULT && (
              <Input
                label={t('importExport.newVaultName')}
                value={newVaultName}
                disabled={importing || !!result}
                onChange={(e) => setNewVaultName(e.target.value)}
              />
            )}
          </div>

          {/* 2. Origen y fichero */}
          <div className="sgc-import__field">
            <label className="sgc-import__label" htmlFor="sgc-import-source">{t('importExport.selectSource')}</label>
            <select id="sgc-import-source" className="sgc-import__select" value={source} disabled={importing} onChange={(e) => setSource(e.target.value)}>
              {SOURCES.map((s) => <option key={s} value={s}>{s === 'auto' ? t('importExport.autoDetect') : SOURCE_NAME[s]}</option>)}
            </select>
            <p className="sgc-import__hint">
              <AlertIcon width={14} height={14} aria-hidden="true" />
              <span>{t(`importExport.instructions.${INSTRUCTION_KEY[source]}`)}</span>
            </p>
            <input ref={fileInputRef} type="file" accept=".csv,.json,.txt,.1pux,.zip,.rfidvault" hidden onChange={handleFile} />
            <PillButton variant="secondary" icon={fileName ? FileIcon : UploadIcon} disabled={importing || !!result} onClick={() => fileInputRef.current?.click()}>
              {fileName || t('importExport.selectFile')}
            </PillButton>
          </div>

          {fileError && <p className="sgc-import__error" role="alert">{fileError}</p>}

          {/* 3. Vista previa */}
          {parsed && !fileError && !result && (
            <div className="sgc-import__preview">
              <h3 className="sgc-import__preview-title">
                {t('importExport.preview')}: {rows.length} {t('importExport.entries')}
              </h3>
              <div className="sgc-import__chips">
                {Object.entries(typeCounts).map(([type, count]) => (
                  <Chip key={type} label={t(`dashboard.categories.${type}`, type)} count={count} />
                ))}
              </div>
              <ul className="sgc-import__list">
                {rows.slice(0, 5).map((r, i) => (
                  <li key={i} className="sgc-import__row">
                    <span className="sgc-import__row-title">{r.entry.title}</span>
                    <span className="sgc-import__row-sub">{r.entry.username}</span>
                    {r.duplicate && <span className="sgc-import__badge">{t('importExport.duplicate')}</span>}
                  </li>
                ))}
              </ul>
              {rows.length > 5 && <p className="sgc-import__hint">+{rows.length - 5} {t('importExport.moreEntries')}</p>}

              {dupLoading && <p className="sgc-import__hint">{t('importExport.checkingDuplicates')}</p>}
              {!dupLoading && dupCount > 0 && (
                <div className="sgc-import__dups">
                  <p className="sgc-import__hint">{t('importExport.duplicatesFound', { count: dupCount })}</p>
                  <SegmentedControl
                    value={dupMode}
                    onChange={setDupMode}
                    options={[
                      { value: 'skip', label: t('importExport.skipDuplicates') },
                      { value: 'import', label: t('importExport.importDuplicates') },
                    ]}
                  />
                </div>
              )}
              {parsed.otpEntries.length > 0 && (
                <p className="sgc-import__hint">
                  {otpBlocked
                    ? t('importExport.otpBlocked')
                    : t('importExport.otpFound', { count: otpSplit.fresh.length, skipped: otpSplit.duplicates.length })}
                </p>
              )}
              <p className="sgc-import__summary">{t('importExport.willImport', { count: toImport.length })}</p>
            </div>
          )}

          {/* 4. Progreso */}
          {importing && (
            <div className="sgc-import__progress">
              <div className="sgc-import__progress-head">
                <span>{t('importExport.importing')} {progress.done}/{progress.total}</span>
                <span>{pct}%</span>
              </div>
              <div className="sgc-import__bar"><div className="sgc-import__bar-fill" style={{ width: `${pct}%` }} /></div>
              <PillButton variant="secondary" size="sm" onClick={() => { cancelRef.current = true; }}>{t('common.cancel')}</PillButton>
            </div>
          )}

          {/* 5. Resultado */}
          {result && (
            <div className={`sgc-import__result ${result.error || result.failed ? 'sgc-import__result--warn' : ''}`}>
              {result.error || result.failed ? <AlertIcon width={20} height={20} aria-hidden="true" /> : <CheckIcon width={20} height={20} aria-hidden="true" />}
              <div>
                <p className="sgc-import__result-title">{t('importExport.importComplete')}</p>
                <p>{result.success} {t('importExport.imported')}, {result.failed} {t('importExport.failed')}</p>
                {result.skippedDuplicates > 0 && <p>{t('importExport.skippedDuplicates', { count: result.skippedDuplicates })}</p>}
                {result.cancelled && <p>{t('importExport.cancelled')}</p>}
                {result.otp && (result.otp.blocked
                  ? <p>{t('importExport.otpBlocked')}</p>
                  : result.otp.saved
                    ? <p>{t('importExport.otpAdded', { count: result.otp.added, skipped: result.otp.skipped })}</p>
                    : <p>{t('importExport.otpSaveFailed')}</p>)}
                {result.error && <p className="sgc-import__error">{String(result.error)}</p>}
              </div>
            </div>
          )}
          {formError && <p className="sgc-import__error" role="alert">{formError}</p>}

          <div className="sgc-import__actions">
            <PillButton variant="secondary" onClick={close} disabled={importing}>{result ? t('importExport.done') : t('common.cancel')}</PillButton>
            {!result && (
              <PillButton icon={UploadIcon} onClick={handleImport} disabled={!canImport} loading={importing}>
                {t('importExport.importNow')}
              </PillButton>
            )}
          </div>
        </div>
      )}

      {tab === 'export' && (
        <div className="sgc-import__body">
          <label className="sgc-import__label">{t('importExport.selectFormat')}</label>
          <div className="sgc-import__formats">
            {exportFormats.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`sgc-import__format ${exportFormat === f.id ? 'sgc-import__format--selected' : ''}`}
                onClick={() => setExportFormat(f.id)}
                aria-pressed={exportFormat === f.id}
              >
                <span className="sgc-import__format-name">{f.name}</span>
                <span className="sgc-import__format-desc">{f.description}</span>
              </button>
            ))}
          </div>
          <p className="sgc-import__hint">
            <AlertIcon width={14} height={14} aria-hidden="true" />
            <span>{t('importExport.exportWarning')}</span>
          </p>
          {exporting && (
            <div className="sgc-import__progress">
              <div className="sgc-import__progress-head"><span>{t('importExport.exporting')}</span><span>{exportPct}%</span></div>
              <div className="sgc-import__bar"><div className="sgc-import__bar-fill" style={{ width: `${exportPct}%` }} /></div>
            </div>
          )}
          {exportError && <p className="sgc-import__error" role="alert">{exportError}</p>}
          <div className="sgc-import__actions">
            <PillButton variant="secondary" onClick={close}>{t('common.cancel')}</PillButton>
            <PillButton icon={DownloadIcon} onClick={handleExport} disabled={exporting} loading={exporting}>{t('importExport.exportNow')}</PillButton>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default ImportExport;
